import { useState } from "react";
import { Camera, Loader2, Plus, CheckCircle2, X, Heart, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, gbp, fmtDate, upload, startCheckout } from "../lib/api";
import { EvidenceList } from "./ComplaintBits";

export function PhotoPicker({ value, onChange, max = 6, testid = "photo-picker" }) {
  const [busy, setBusy] = useState(false);
  const add = async (files) => {
    const list = Array.from(files).slice(0, max - value.length);
    if (!list.length) return toast.error(`You can add up to ${max} photos`);
    setBusy(true);
    try { const out = []; for (const f of list) out.push(await upload(f)); onChange([...value, ...out]); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="inline-flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-600 cursor-pointer hover:border-[#FF8166] transition-colors" data-testid={testid}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}{busy ? "Uploading…" : `Add photos (${value.length}/${max})`}
        <input type="file" multiple accept="image/*" className="hidden" disabled={busy} onChange={(e) => add(e.target.files)} data-testid={`${testid}-input`} />
      </label>
      <EvidenceList items={value} onRemove={(i) => onChange(value.filter((_, j) => j !== i))} />
    </div>
  );
}

export function ReleaseForm({ url, onDone, onCancel, testid }) {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(url, { photos, note }); toast.success("Homeowner asked to approve this payment"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-slate-50 p-3 mt-2 space-y-2 w-full" data-testid={testid}>
      <div className="text-sm font-semibold">Show the homeowner the finished work</div>
      <PhotoPicker value={photos} onChange={setPhotos} testid={`${testid}-photos`} />
      <textarea className="input text-sm" rows={2} placeholder="Short note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testid}-note`} />
      <div className="flex gap-2">
        <button onClick={send} disabled={busy || !photos.length} className="btn-navy btn-sm" data-testid={`${testid}-submit`}>{photos.length ? "Send for approval" : "Add at least 1 photo"}</button>
        <button onClick={onCancel} className="btn-ghost btn-sm" data-testid={`${testid}-cancel`}>Cancel</button>
      </div>
    </div>
  );
}

export function ReleaseProof({ esc }) {
  if (!esc?.release_photos?.length) return null;
  return (<div className="w-full" data-testid="release-proof">{esc.release_note && <p className="text-xs text-slate-600 mt-1 italic">“{esc.release_note}”</p>}<EvidenceList items={esc.release_photos} /></div>);
}

const X_STATUS = { proposed: ["Waiting for answer", "bg-amber-50 text-amber-800"], accepted: ["Agreed, not paid", "bg-sky-50 text-sky-800"], declined: ["Declined", "bg-slate-100 text-slate-500"], withdrawn: ["Withdrawn", "bg-slate-100 text-slate-500"] };
const E_STATUS = { held: ["Held securely", "bg-emerald-50 text-emerald-700"], requested: ["Awaiting approval", "bg-amber-50 text-amber-800"], on_hold: ["On hold (complaint)", "bg-rose-50 text-rose-700"], released: ["Released", "bg-[#374B5C] text-white"], refunded: ["Refunded", "bg-slate-200 text-slate-700"] };
const escState = (e) => e.status !== "held" ? e.status : e.on_hold ? "on_hold" : e.release_requested_at ? "requested" : "held";

function ProposeExtra({ jobId, role, onDone }) {
  const [x, setX] = useState({ description: "", amount: "" });
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const send = async () => {
    try { await api.post(`/jobs/${jobId}/extras`, { ...x, amount: +x.amount, photos }); toast.success("Extra work sent for agreement"); setX({ description: "", amount: "" }); setPhotos([]); setOpen(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="btn-ghost btn-sm" data-testid="extra-propose-open"><Plus size={14} />{role === "provider" ? "Add extra work" : "Request extra work"}</button>;
  return (
    <div className="rounded-xl bg-slate-50 p-3 space-y-2" data-testid="extra-propose-form">
      <div className="grid sm:grid-cols-[1fr_130px] gap-2">
        <input className="input py-2" placeholder={role === "provider" ? "e.g. Replace corroded valve found during the job" : "e.g. Also fit a new outside tap"} value={x.description} onChange={(e) => setX({ ...x, description: e.target.value })} data-testid="extra-desc" />
        <input type="number" className="input py-2" placeholder="Price £" value={x.amount} onChange={(e) => setX({ ...x, amount: e.target.value })} data-testid="extra-amount" />
      </div>
      <PhotoPicker value={photos} onChange={setPhotos} testid="extra-photos" />
      <div className="flex gap-2"><button onClick={send} disabled={x.description.length < 5 || !(+x.amount >= 1)} className="btn-navy btn-sm" data-testid="extra-submit">Send</button><button onClick={() => setOpen(false)} className="btn-ghost btn-sm">Cancel</button></div>
    </div>
  );
}

function ExtraRow({ x, role, onDone }) {
  const [releasing, setReleasing] = useState(false);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const mine = x.proposed_by === role;
  const e = x.escrow;
  const [label, cls] = e ? E_STATUS[escState(e)] || E_STATUS.held : X_STATUS[x.status] || X_STATUS.proposed;
  const canPay = role === "homeowner" && !e && (x.status === "accepted" || (x.status === "proposed" && x.proposed_by === "provider"));
  const pay = () => startCheckout({ kind: "job_extra", ref: x.id }).catch((er) => toast.error(errMsg(er)));
  return (
    <div className="py-3 border-t border-slate-100 first:border-0" data-testid={`extra-row-${x.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1"><div className="text-sm font-semibold">{x.description}</div><div className="text-xs muted">Proposed by {mine ? "you" : x.proposed_by === "provider" ? "the professional" : "the homeowner"} · {fmtDate(x.created_at)}</div></div>
        <span className="font-bold text-sm">{gbp(x.amount)}</span>
        <span className={`chip text-xs px-2.5 py-1 ${cls}`} data-testid="extra-status">{label}</span>
        {canPay && <button onClick={pay} className="btn-navy btn-sm" data-testid="extra-pay-btn">{x.status === "proposed" ? "Accept & pay" : "Pay"} {gbp(x.amount)}</button>}
        {!mine && x.status === "proposed" && role === "provider" && <button onClick={() => act(() => api.post(`/extras/${x.id}/respond`, { accept: true }), "Extra accepted")} className="btn-navy btn-sm" data-testid="extra-accept-btn"><CheckCircle2 size={14} />Accept</button>}
        {!mine && x.status === "proposed" && <button onClick={() => act(() => api.post(`/extras/${x.id}/respond`, { accept: false }), "Extra declined")} className="btn-ghost btn-sm text-rose-600" data-testid="extra-decline-btn">Decline</button>}
        {mine && ["proposed", "accepted"].includes(x.status) && !e && <button onClick={() => act(() => api.post(`/extras/${x.id}/cancel`), "Extra withdrawn")} className="btn-ghost btn-sm" data-testid="extra-cancel-btn"><X size={14} />Withdraw</button>}
        {e && role === "provider" && escState(e) === "held" && !releasing && <button onClick={() => setReleasing(true)} className="btn-navy btn-sm" data-testid="extra-request-btn">Work done, request payment</button>}
        {e && role === "homeowner" && ["held", "requested"].includes(escState(e)) && <button onClick={() => window.confirm(`Release ${gbp(x.amount)}?`) && act(() => api.post(`/extras/${x.id}/approve`), "Payment released")} className="btn-coral btn-sm" data-testid="extra-approve-btn"><CheckCircle2 size={14} />Approve &amp; release</button>}
      </div>
      {x.photos?.length > 0 && <EvidenceList items={x.photos} />}
      {e && escState(e) === "requested" && <div className="text-xs text-amber-800 mt-1">Released automatically on {fmtDate(e.auto_release_at)} unless a problem is raised</div>}
      <ReleaseProof esc={e} />
      {releasing && <ReleaseForm url={`/extras/${x.id}/request-release`} testid="extra-release-form" onDone={() => { setReleasing(false); onDone(); }} onCancel={() => setReleasing(false)} />}
    </div>
  );
}

export function ExtrasCard({ job, role, onDone }) {
  const list = job.extras || [];
  const open = job.status !== "cancelled";
  if (!open && !list.length) return null;
  return (
    <div className="card p-5" data-testid="extras-card">
      <h2 className="font-bold flex items-center gap-2"><Wrench size={16} className="text-[#FF8166]" />Extra work</h2>
      <p className="text-xs muted mt-1 mb-2">Agree any extra work here before it's done. Payments are held securely and released when the homeowner approves, or automatically after the waiting period once the professional sends photos.</p>
      {list.map((x) => <ExtraRow key={x.id} x={x} role={role} onDone={onDone} />)}
      {open && <div className="mt-3"><ProposeExtra jobId={job.id} role={role} onDone={onDone} /></div>}
    </div>
  );
}

export function TipBox({ job, provider }) {
  const [amount, setAmount] = useState(10);
  const [note, setNote] = useState("");
  const tips = job.tips || [];
  if (job.status !== "completed") return null;
  const pay = () => startCheckout({ kind: "job_tip", ref: job.id, amount: +amount, note }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="card p-5 bg-[#FFF6F3] border-[#FFD9CF]" data-testid="tip-box">
      <h2 className="font-bold flex items-center gap-2"><Heart size={16} className="text-[#FF8166]" />Say thanks with a tip</h2>
      <p className="text-xs muted mt-1">100% of your tip goes to {provider}. It's sent straight away.</p>
      {tips.length > 0 && <div className="text-sm mt-2" data-testid="tips-given">{tips.map((t) => <div key={t.id}>You tipped <b>{gbp(t.amount)}</b> on {fmtDate(t.at)}</div>)}</div>}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {[5, 10, 20].map((n) => <button key={n} onClick={() => setAmount(n)} className={`chip px-3 py-1.5 ${+amount === n ? "bg-[#374B5C] text-white" : "bg-white border border-slate-200"}`} data-testid={`tip-preset-${n}`}>{gbp(n)}</button>)}
        <input type="number" className="input py-1.5 w-28" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tip-amount" />
      </div>
      <input className="input mt-2 text-sm" placeholder="Add a thank-you message (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="tip-note" />
      <button onClick={pay} disabled={!(+amount >= 1)} className="btn-coral btn-sm mt-3" data-testid="tip-pay-btn">Send {gbp(amount)} tip</button>
    </div>
  );
}

export function TipsReceived({ tips }) {
  if (!tips?.length) return null;
  return (<div className="rounded-xl bg-[#FFF6F3] p-3 text-sm" data-testid="tips-received"><div className="font-semibold flex items-center gap-1.5"><Heart size={14} className="text-[#FF8166]" />Tips received</div>
    {tips.map((t) => <div key={t.id} className="mt-1">{gbp(t.amount)} on {fmtDate(t.at)}{t.message && <span className="italic text-slate-600"> · “{t.message}”</span>}</div>)}</div>);
}
